/**
 * Tints a variant pill's border with the dominant colour of that variant's photo.
 *
 * This only runs for option values that are NOT colour words — rose varieties like
 * "Frutetto" or "Be Sweet". Plain colour names are resolved server-side in Liquid
 * (see 'fs-colour-map'), so those pills are already painted before this file runs.
 *
 * Results are saved to localStorage, so a photo is sampled once per shopper and
 * every later page load paints synchronously with no network request and no flash.
 */
(() => {
  if (window.__fsVariantBorderColour) return;
  window.__fsVariantBorderColour = true;

  const STORE_KEY = 'fs-variant-border-colour-v1';
  const STORE_LIMIT = 500;
  const SIZE = 32;
  const CENTRE = 0.2; // ignore the outer 20%, usually wrapping, stems and background
  const MAX_LIGHTNESS = 0.58; // dark enough to read, without recolouring pale flowers
  const MIN_SATURATION = 0.38; // lift washed-out colours so the ring looks deliberate
  const SATURATION_FLOOR_FROM = 0.2; // but leave naturally muted colours (cream, dusty
  // pinks, greys) alone: boosting them turns cream into khaki
  const MIN_CONFIDENCE = 0.12; // the winning colour must hold at least this share of the
  // sampled pixels. Wide shots of boxes and bunches have no dominant colour at all, and
  // a confident-looking wrong colour is worse than no colour: below this we draw nothing.
  const PALE_FROM = 0.87; // above this the source is white/cream/ivory
  const PALE_SATURATION = 0.16;
  const PALE_LIGHTNESS = 0.6;

  /** Colours already resolved this page load. Values are strings or Promises. */
  const memory = new Map();

  const readStore = () => {
    try {
      return JSON.parse(localStorage.getItem(STORE_KEY) || '{}');
    } catch (error) {
      return {};
    }
  };

  const writeStore = (url, colour) => {
    try {
      const store = readStore();
      const keys = Object.keys(store);
      // Cheap eviction: once full, drop the oldest half rather than growing forever.
      if (keys.length >= STORE_LIMIT) {
        keys.slice(0, Math.floor(STORE_LIMIT / 2)).forEach((key) => delete store[key]);
      }
      store[url] = colour;
      localStorage.setItem(STORE_KEY, JSON.stringify(store));
    } catch (error) {
      /* private mode or quota: the in-memory cache still works for this page */
    }
  };

  const toHsl = (r, g, b) => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) return [0, 0, l];
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
    return [h, s, l];
  };

  /** Darken and saturate so the ring reads clearly whatever the photo looked like. */
  const readable = (r, g, b) => {
    let [h, s, l] = toHsl(r, g, b);
    if (l > PALE_FROM) {
      // A white or cream flower has no dark version of itself: darkening it enough to
      // be visible turns it khaki. Read it as a warm neutral instead, which is closer
      // to how the eye reads the flower than a tan would be.
      s = Math.min(s, PALE_SATURATION);
      l = PALE_LIGHTNESS;
    } else {
      if (l > MAX_LIGHTNESS) l = MAX_LIGHTNESS;
      if (s > SATURATION_FLOOR_FROM && s < MIN_SATURATION) s = MIN_SATURATION;
    }
    return `hsl(${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%)`;
  };

  /**
   * Reads the dominant colour by bucketing central pixels and weighting each by
   * saturation, so a vivid bloom beats a large flat area of stem or paper.
   */
  const dominantColour = (img) => {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = SIZE;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, SIZE, SIZE);

    const pad = Math.floor(SIZE * CENTRE);
    const data = ctx.getImageData(pad, pad, SIZE - pad * 2, SIZE - pad * 2).data;
    const buckets = new Map();

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      if (data[i + 3] < 200) continue;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      if (max > 246 && min > 246) continue; // blown-out highlight
      if (max < 26) continue; // near black

      const saturation = max === 0 ? 0 : (max - min) / max;
      const weight = 1 + saturation * 4;
      const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
      const bucket = buckets.get(key) || { w: 0, r: 0, g: 0, b: 0 };
      bucket.w += weight;
      bucket.r += r * weight;
      bucket.g += g * weight;
      bucket.b += b * weight;
      buckets.set(key, bucket);
    }

    let best = null;
    let total = 0;
    buckets.forEach((bucket) => {
      total += bucket.w;
      if (!best || bucket.w > best.w) best = bucket;
    });
    if (!best) return null;
    if (total === 0 || best.w / total < MIN_CONFIDENCE) return null;

    return readable(
      Math.round(best.r / best.w),
      Math.round(best.g / best.w),
      Math.round(best.b / best.w)
    );
  };

  const sample = (url) => {
    if (memory.has(url)) return memory.get(url);

    const pending = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        let colour = null;
        try {
          colour = dominantColour(img);
        } catch (error) {
          colour = null; // tainted canvas or decode failure: keep the theme default
        }
        if (colour) writeStore(url, colour);
        resolve(colour);
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });

    memory.set(url, pending);
    return pending;
  };

  // Matches the "L%)" tail of the hsl(H S% L%) string `readable()` produces above,
  // so the selected pill's solid fill (same colour) gets text that stays legible
  // against it, without re-deriving lightness from scratch.
  const LIGHTNESS_RE = /(\d+)%\)$/;
  const TEXT_LIGHTNESS_THRESHOLD = 55;

  const apply = (label, colour) => {
    if (!colour) return;
    label.style.setProperty('--color-variant-border', colour);

    const match = LIGHTNESS_RE.exec(colour);
    const lightness = match ? parseInt(match[1], 10) : 0;
    label.style.setProperty(
      '--color-selected-variant-text',
      lightness > TEXT_LIGHTNESS_THRESHOLD ? 'var(--color-foreground)' : '#fff'
    );
  };

  const paint = () => {
    const labels = document.querySelectorAll('[data-fs-border-src]');
    if (!labels.length) return;
    const store = readStore();

    labels.forEach((label) => {
      // The theme morphs this markup on every variant change, which drops inline
      // styles, so test the style itself rather than a "done" flag.
      if (label.style.getPropertyValue('--color-variant-border')) return;

      const url = label.dataset.fsBorderSrc;
      const known = memory.get(url) || store[url];

      // Anything already known is applied synchronously, so there is no flash.
      if (typeof known === 'string') {
        memory.set(url, known);
        apply(label, known);
        return;
      }

      sample(url).then((colour) => apply(label, colour));
    });
  };

  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      paint();
    });
  };

  paint(); // synchronous first pass, so cached colours are there immediately
  document.addEventListener('DOMContentLoaded', schedule);
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
